import React, { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useIntl } from "react-intl";

import { mapAlarmStatusToCategory } from "../../../../../../../../../utils/alarm-colors";
import { formatMeasurementNumber } from "../../../../../utils/measurement-format";
import { useUser } from "@framework/provider-user";
import { IUser, STATION_TRAIN_MEASURE_ROWS_LIMIT } from "@framework/types";
import { IStationTrainMeasureRow } from "../interfaces";

export interface IStationTrainMeasuresTableProps {
  rows: IStationTrainMeasureRow[];
}

const CELL_BORDER = "1px solid #DADDE5";
const CELL_RADIUS = "2px";
const EMPTY_VALUE_BACKGROUND = "#FFFFFF";
const VALUE_TEXT_COLOR = "#000000";

const MEASURE_TOOLTIP_POPPER_PROPS = {
  sx: {
    "& .MuiTooltip-tooltip": {
      m: 0,
      px: 1,
      py: 0.75,
      maxWidth: 280,
      color: "#202534",
      backgroundColor: "#FFFFFF",
      border: "1px solid #8A93A8",
      borderRadius: "4px",
      boxShadow: "0px 4px 12px rgba(49, 57, 102, 0.24)",
      fontSize: 12,
      lineHeight: "16px",
      overflowWrap: "anywhere",
    },
    "& .MuiTooltip-arrow": {
      color: "#FFFFFF",
      fontSize: 14,
      filter: "drop-shadow(0px 2px 2px rgba(49, 57, 102, 0.24))",
      "&::before": {
        boxSizing: "border-box",
        border: "1px solid #8A93A8",
      },
    },
  },
} as const;
const UNIT_PATTERN = /(мкм|мм\/с|м\/с²|м\/с2|g|°C|kPa|bar|A|V|Hz|%)/iu;

const STATION_TRAIN_MEASURE_ALARM_BACKGROUNDS = {
  notDefined: "#FFFFFF",
  noData: "#C0C0C0",
  normal: "#B0D9B5",
  attention: "#F9F19A",
  warning: "#F9F19A",
  alarm: "#F79E9E",
  invalid: "#F5C6F5",
  pickupFailed: "#A8C8FF",
} as const;

const stopTooltipEventPropagation: React.MouseEventHandler<HTMLElement> = event => {
  event.stopPropagation();
};

interface IStationTrainMeasureCellProps {
  cellSx: SxProps<Theme>;
  textSx: SxProps<Theme>;
  displayText: string;
  tooltipText: string;
  alwaysShowTooltip?: boolean;
  stopParentTooltipEvents?: boolean;
}

const StationTrainMeasureCell: FC<IStationTrainMeasureCellProps> = ({
  cellSx,
  textSx,
  displayText,
  tooltipText,
  alwaysShowTooltip = false,
  stopParentTooltipEvents = false,
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const textElement = textRef.current;
    if (!textElement) {
      return;
    }

    const updateOverflow = () => {
      setIsOverflowing(textElement.scrollWidth > textElement.clientWidth);
    };

    updateOverflow();

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(textElement);

    return () => observer.disconnect();
  }, [displayText]);

  const isTooltipEnabled = alwaysShowTooltip || isOverflowing;

  return (
    <Tooltip
      arrow
      disableInteractive
      title={tooltipText}
      PopperProps={MEASURE_TOOLTIP_POPPER_PROPS}
      disableHoverListener={!isTooltipEnabled}
      disableFocusListener={!isTooltipEnabled}
      disableTouchListener={!isTooltipEnabled}
    >
      <Box
        sx={cellSx}
        onMouseOut={stopParentTooltipEvents ? stopTooltipEventPropagation : undefined}
        onMouseOver={stopParentTooltipEvents ? stopTooltipEventPropagation : undefined}
      >
        <Typography ref={textRef} sx={textSx}>
          {displayText}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const resolveStationTrainMeasureBackground = (alarmStatus: number): string => {
  const category = mapAlarmStatusToCategory(alarmStatus);
  return STATION_TRAIN_MEASURE_ALARM_BACKGROUNDS[category];
};

const LABEL_CELL_SX = {
  flex: "1 1 54%",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: 0.75,
  minHeight: 24,
  border: CELL_BORDER,
  borderRadius: CELL_RADIUS,
  backgroundColor: "#FFFFFF",
  overflow: "hidden",
} as const;

const VALUE_CELL_SX = {
  flex: "0 0 46%",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: 0.75,
  minHeight: 24,
  border: CELL_BORDER,
  borderRadius: CELL_RADIUS,
  overflow: "hidden",
} as const;

const formatMeasureLabel = (
  measureName: string,
  pointName: string,
  units: number | null | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string => {
  const name = measureName.trim().replace(/\*+$/u, "");
  const point = pointName.trim();
  const pointWithoutUnit = point
    .replace(UNIT_PATTERN, "")
    .replace(/\s{2,}/gu, " ")
    .trim();
  const pointSuffix = pointWithoutUnit ? ` / ${pointWithoutUnit}` : "";

  if (typeof units === "number") {
    const unitLabel = formatMessage({ id: `enums.units.${units}` });
    return UNIT_PATTERN.test(name) ? `${name}${pointSuffix}:` : `${name} ${unitLabel}${pointSuffix}:`;
  }

  const unitMatch = pointName.match(UNIT_PATTERN);
  if (unitMatch?.[0]) {
    return UNIT_PATTERN.test(name) ? `${name}${pointSuffix}:` : `${name} ${unitMatch[0]}${pointSuffix}:`;
  }

  return `${name}${pointSuffix}:`;
};

const formatTrendArrow = (trend: IStationTrainMeasureRow["trend"]): string => {
  if (trend === "up") return "↑";
  if (trend === "down") return "↓";
  if (trend === "flat") return "→";
  return "";
};

export const formatStationTrainMeasureValue = (
  row: IStationTrainMeasureRow,
  dataFormat: IUser["interfaceSettings"]["dataFormat"],
): { displayText: string; tooltipText: string } => {
  if (typeof row.value !== "number") {
    return { displayText: "—", tooltipText: "—" };
  }

  const formattedValue = formatMeasurementNumber(row.value, row.units ?? null, dataFormat);
  const arrow = formatTrendArrow(row.trend);

  if (typeof row.delta !== "number" || !arrow) {
    return { displayText: formattedValue, tooltipText: formattedValue };
  }

  const formattedDelta = formatMeasurementNumber(Math.abs(row.delta), row.units ?? null, dataFormat);
  const signedDelta = row.delta > 0 ? `+${formattedDelta}` : row.delta < 0 ? `-${formattedDelta}` : formattedDelta;
  const displayText = row.trend === "up" || row.trend === "down" ? `${formattedValue} (${arrow})` : formattedValue;

  return {
    displayText,
    tooltipText: `${formattedValue} (${arrow}${signedDelta})`,
  };
};

export const StationTrainMeasuresTable: FC<IStationTrainMeasuresTableProps> = ({ rows }) => {
  const { formatMessage } = useIntl();
  const user = useUser<IUser>();

  const displayRows = useMemo(() => {
    const filled = rows.slice(0, STATION_TRAIN_MEASURE_ROWS_LIMIT);
    const placeholders = Array.from(
      { length: Math.max(0, STATION_TRAIN_MEASURE_ROWS_LIMIT - filled.length) },
      () => null,
    );
    return [...filled, ...placeholders];
  }, [rows]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "0px",
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {displayRows.map((row, index) => {
        if (!row) {
          return (
            <Box key={`empty-${index}`} sx={{ display: "flex", gap: "1px", width: "100%" }}>
              <Box sx={LABEL_CELL_SX} />
              <Box sx={{ ...VALUE_CELL_SX, backgroundColor: EMPTY_VALUE_BACKGROUND }} />
            </Box>
          );
        }

        const label = formatMeasureLabel(row.measureName, row.pointName, row.units, formatMessage);
        const backgroundColor = resolveStationTrainMeasureBackground(row.alarmStatus ?? 0);
        const formattedValue = formatStationTrainMeasureValue(row, user.profile.interfaceSettings.dataFormat);
        const hasDeltaTooltip = formattedValue.displayText !== formattedValue.tooltipText;

        return (
          <Box
            key={`${row.measureName}-${row.units ?? "x"}-${index}`}
            sx={{ display: "flex", gap: "1px", width: "100%" }}
          >
            <StationTrainMeasureCell
              cellSx={LABEL_CELL_SX}
              displayText={label}
              tooltipText={label}
              textSx={{
                fontSize: 13,
                lineHeight: "16px",
                fontWeight: 400,
                color: "#777777",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
                width: "100%",
              }}
            />
            <StationTrainMeasureCell
              alwaysShowTooltip={hasDeltaTooltip}
              cellSx={{ ...VALUE_CELL_SX, backgroundColor }}
              displayText={formattedValue.displayText}
              stopParentTooltipEvents={hasDeltaTooltip}
              tooltipText={formattedValue.tooltipText}
              textSx={{
                fontSize: 14,
                lineHeight: "17px",
                fontWeight: 700,
                color: VALUE_TEXT_COLOR,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
                width: "100%",
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};
